/**
 * shadcn/ui 風 Dialog を非React環境向けに制御するユーティリティ
 * - マークアップ要件:
 *   <div id="modal" data-shadcn-dialog data-state="closed" class="hidden fixed inset-0 z-50">
 *     <div data-dialog-overlay class="fixed inset-0 bg-black/80"></div>
 *     <div data-dialog-content role="dialog" aria-modal="true"></div>
 *   </div>
 * - 閉じるトリガー: 要素に [data-dialog-close] を付与
 */
export const ShadcnDialog = {
  /** すでに開いているDialogを全て閉じる（必要に応じて削除） */
  closeAll(remove = false) {
    const dialogs = document.querySelectorAll<HTMLElement>(
      '[data-shadcn-dialog][data-state="open"]'
    );
    dialogs.forEach((el) => ShadcnDialog.close(el.id || undefined, remove));
  },

  /** 指定IDまたは要素でDialogを開く */
  open(target?: string | HTMLElement) {
    const el = ShadcnDialog._resolveEl(target);
    if (!el) return;

    // 他のDialogは閉じる
    ShadcnDialog.closeAll(true);

    el.classList.remove('hidden');
    el.setAttribute('data-state', 'open');
    document.body.classList.add('overflow-hidden');

    // イベントバインド（1回のみ）
    if (!el.dataset.bound) {
      ShadcnDialog._bindEvents(el);
      el.dataset.bound = '1';
    }

    // フォーカスを中身に移動
    const content = el.querySelector<HTMLElement>('[data-dialog-content]');
    content?.focus?.();
  },

  /** 指定IDまたは要素でDialogを閉じる */
  close(target?: string | HTMLElement, remove = false) {
    const el = ShadcnDialog._resolveEl(target);
    if (!el) return;

    el.setAttribute('data-state', 'closed');
    document.body.classList.remove('overflow-hidden');
    el.classList.add('hidden');
    el.dispatchEvent(
      new CustomEvent('shadcn:dialog:closed', { bubbles: true })
    );

    if (remove) el.remove();
  },

  /** 呼び出し元要素から最も近いDialogを閉じる */
  closeClosest(fromEl: Element, remove = false) {
    const dialog = fromEl.closest<HTMLElement>('[data-shadcn-dialog]');
    if (dialog) ShadcnDialog.close(dialog, remove);
  },

  /** ID/要素解決 */
  _resolveEl(target?: string | HTMLElement): HTMLElement | null {
    if (!target)
      return document.querySelector<HTMLElement>('[data-shadcn-dialog]');
    if (typeof target === 'string') return document.getElementById(target);
    return target;
  },

  /** イベントバインド */
  _bindEvents(root: HTMLElement) {
    const overlay = root.querySelector('[data-dialog-overlay]');
    overlay?.addEventListener('click', () => ShadcnDialog.close(root));

    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-dialog-close]')) {
        ShadcnDialog.close(root);
      }
    });

    root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        ShadcnDialog.close(root);
      }
    });
  },
};

// グローバルに公開
declare global {
  interface Window {
    ShadcnDialog: typeof ShadcnDialog;
  }
}
window.ShadcnDialog = ShadcnDialog;

export default ShadcnDialog;
